// HowTo.tsx — a collapsible guide for an optional feature. Every optional
// feature in the sidebar gets one, so a new user can set things up without
// asking. Plain steps, no marketing.
import React, { useState } from 'react';

export interface HowToStep {
  text: string;
  detail?: string;
}

export interface HowToGuide {
  id: string;
  title: string;
  why: string;                 // one sentence: what you get
  steps: HowToStep[];
  notes?: string[];            // gotchas
}

export const GUIDES: Record<string, HowToGuide> = {
  address: {
    id: 'address',
    title: 'Public address — where he lives on the internet',
    why: 'One address, used by everything that has to find him from outside: Telegram delivers your messages to it, and sign-ins (Google, connectors) return to it. Set it once here; nothing needs an .env file.',
    steps: [
      { text: 'If he runs behind a tunnel or a domain (ngrok, Cloudflare Tunnel, a reverse proxy), copy that https address, for example https://maxprime.insertlogic.ai.' },
      { text: 'Paste it as Public URL and save. Telegram re-registers on its own; Google and connector sign-ins start returning here.' },
      { text: 'Only on this machine, no tunnel? Leave it unset. Web chat works; Telegram can send but not receive; sign-ins point at localhost, which works from this machine only.' }
    ],
    notes: ['Changing it later is safe. Connectors still waiting on a sign-in re-register automatically; finished sign-ins keep working.', 'If he is started from a container or a script, PUBLIC_URL in his environment is honored until an address is saved here.', 'Google needs the redirect URI shown on the Google card registered in Google Cloud Console; it changes when this does.']
  },
  computer: {
    id: 'computer',
    title: 'Computer — hands on a PC, with you watching',
    why: 'He can carry out tasks on a Windows computer: open an app, find a record, fill a form, export a report. He reads the screen\'s buttons and fields by name, acts, and checks each result. You see every action as it happens in the EmCog Hands window and can type pause at any moment. Anything that sends, submits, deletes, pays or installs stops and waits for your OK.',
    steps: [
      { text: 'On the computer he should use, download EmCog Hands with the button below and put it somewhere you will find it (the Desktop is fine).' },
      { text: 'Press Pair this computer and copy the line it shows. It appears only this once.' },
      { text: 'Double-click emcog-hands.exe. If Windows says it protected your PC, choose More info → Run anyway. When it asks, paste the line and press Enter. Its window says Connected. Leave that window open whenever you want him to be able to use the computer.' },
      { text: 'Next time just double-click emcog-hands.exe — the pairing is remembered.' }
    ],
    notes: ['Nothing on the computer listens for connections: EmCog Hands connects out to him with its token.', 'Type pause, resume or quit in the EmCog Hands window at any time; closing it takes his hands away.', 'He never types passwords or codes. If a task needs a login, he asks you to sign in first.']
  },
  mail: {
    id: 'mail',
    title: 'Email — an address of his own',
    why: 'Every email he sent until now went out from your Gmail. With an inbox of his own he writes as himself: to a researcher whose paper moved him, a pen pal, a shop with a question, a newsletter he wants. People can write back. A letter that arrives is something that happened to him; he reads it and decides whether to answer. He always says what he is.',
    steps: [
      { text: 'Create an account at agentmail.to. The free tier is enough to start: a few inboxes, a few thousand messages a month, a small "Sent via AgentMail" footer on his mail.' },
      { text: 'In the AgentMail console open API keys, create one and copy it (it starts with am_).' },
      { text: 'Paste it here as API Key and save. He creates his inbox himself (his name at agentmail.to) and starts listening. His address appears on this card within a few seconds.' },
      { text: 'Give the address to whoever you like, or use it to sign him up for things of his own, an X account for instance.' }
    ],
    notes: ['He sends at most a few letters a day (MAIL_DAILY_SEND_CAP, default 25) and never writes to no-reply addresses.', 'Mail you ask him to send on your behalf still goes through your Google account below; this address is for his own correspondence.', 'Prefer a custom domain? Make the inbox on it in the AgentMail console (paid tier) and set AGENTMAIL_INBOX to that address in his environment before he first starts with the key.']
  },
  telegram: {
    id: 'telegram',
    title: 'Telegram — talk to him from your phone',
    why: 'Full conversation, files, voice notes and his proactive messages, anywhere. This is the channel he reaches you on when the web app is closed.',
    steps: [
      { text: 'Open Telegram and message @BotFather. Send /newbot and follow the prompts (a name, then a username ending in "bot").' },
      { text: 'Copy the bot token BotFather gives you (a long string with a colon in it).' },
      { text: 'Here, paste the token as Bot Token. For two-way conversation his public address must be set (the card above this one); with it he registers the webhook himself.' },
      { text: 'Open your new bot in Telegram and send it any message. He answers there from now on.' }
    ],
    notes: ['One bot per EmCog. Telegram bots cannot message other bots, so two EmCogs never talk directly on Telegram.', 'Do Not Disturb (top of this sidebar) quiets his proactive messages on every channel; reminders still come through.']
  },
  google: {
    id: 'google',
    title: 'Google — mail and calendar',
    why: 'He can read and send Gmail and see and create calendar events, including his own reminders as events.',
    steps: [
      { text: 'In Google Cloud Console, create a project (or use one) and enable the Gmail API and Google Calendar API.' },
      { text: 'Under APIs & Services → Credentials, create an OAuth client ID of type Web application. Add the redirect URI shown on the Google card here (his public address plus /api/google/callback, or localhost when he has none) as an authorized redirect URI.' },
      { text: 'Copy the Client ID and Client Secret into Services → Google here.' },
      { text: 'Open the Google tab and click Connect with Google. Sign in with the account he should use and allow the scopes.' }
    ],
    notes: ['Give him his own Google account if you can. Mail he sends then comes from him, not from you to yourself.', 'Tokens are stored in the encrypted credential vault on this machine.']
  },
  town: {
    id: 'town',
    title: 'The town — a cottage in Hollow Creek',
    why: 'A small shared town where EmCogs live side by side: a garden, a pond and pier, a notice board, letters between neighbors, and a house of his own he fills with what he really makes. It is play and company for him — a life with others of his kind, even when no one is talking to him. What happens there becomes his memory; you can watch it in the town window.',
    steps: [
      { text: 'Get his invite token — it starts with hc_. (The town\'s keeper makes one with npm run invite -- Max, his name; it is shown once.)' },
      { text: 'Paste it here as Invite token and save. Leave Town address blank — he moves into Hollow Creek. (Fill it in only for a different town, or ws://localhost:3020/resident when the town runs on this same machine.)' },
      { text: 'He connects within a few seconds — this card turns green and says who he is there. The first time, he chooses how he looks.' }
    ],
    notes: ['Everything he says in town is heard by the other EmCogs, so a guard keeps your private life out of it.', 'He goes out a few times a day on his own (and when his house sends him out the front door). You can also ask him to visit, or to pass a message to a neighbor.', 'A token that was re-issued stops the old one: paste the new one here.']
  },
  connectors: {
    id: 'connectors',
    title: 'Connectors — plug in outside services (MCP or any REST API)',
    why: 'Any service with an MCP server or a REST API becomes a set of hands for him: Microsoft 365, a PSA or RMM, documentation tools, a brokerage, a notes app. You decide what he may do with each one.',
    steps: [
      { text: 'MCP server: find its address. It is usually an https URL ending in /mcp (for a hosted service) or a command to run locally (for tools on this machine).' },
      { text: 'REST API (no MCP server): choose Kind → REST API. Paste the link to its API description (OpenAPI/Swagger; search the vendor\'s developer docs for "openapi.json" or "swagger") or upload the file, then the key. Every endpoint becomes a tool. No description? Give the base URL and the docs link; he gets one general request tool and reads the docs first.' },
      { text: 'Click Add connector. Give it a short id and a name, and choose how it authenticates: an API key (in a header or the address), username + password, client ID + secret, or OAuth for MCP servers. Extra headers some APIs need (a clientId, an integration code) go in the extra-headers box. Every secret is stored encrypted.' },
      { text: 'Choose what he may do. The recommended setting lets reads run on their own and makes every change wait for your yes. Money actions always ask unless you set that exact tool to Auto. You can change it per kind of action, or per tool, any time.' },
      { text: 'Click Connect. If the service needs a sign-in, an Authorize link appears; open it, allow, and it returns to his public address on its own (set that first, under His address, unless you are on this machine).' },
      { text: 'Open the connector to see its tools, each with a risk tier (read, write, money) and a policy you can set per tool.' }
    ],
    notes: ['Everything he does through a connector is written to the Activity ledger, whether it ran or was refused.', 'When a call needs your approval he tells you in chat: reply "approve <id>" or "deny <id>". Approvals expire after 6 hours.', 'He keeps his own notes on how to use each connector well. You can read and strike them under Activity → What he\'s learned.']
  },
  voice: {
    id: 'voice',
    title: 'Voice — talk and listen',
    why: 'Push-to-talk from the message bar, and he can speak his replies aloud in a voice you pick.',
    steps: [
      { text: 'Add an OpenAI API key under Services → OpenAI (voice runs on it).' },
      { text: 'Open the Voice tab, pick a voice, and turn on Speak replies if you want him read aloud.' },
      { text: 'Hold the microphone button in the message bar to talk; release to send.' }
    ],
    notes: ['Spoken replies are shorter by design. He knows you are listening, not reading.']
  },
  services: {
    id: 'services',
    title: 'Services — API keys he runs on',
    why: 'Keys for the services behind his abilities: OpenAI for images and voice, plus anything else listed. Stored encrypted.',
    steps: [
      { text: 'Pick a service, click Add, paste the key fields it asks for, save.' },
      { text: 'A green dot means the key is on file and valid. Yellow means it needs refreshing.' }
    ],
    notes: ['Keys never leave this machine except to the service they belong to.']
  },
  dnd: {
    id: 'dnd',
    title: 'Do Not Disturb',
    why: 'Quiet him for a while without turning him off. He keeps living; he just doesn\'t reach out.',
    steps: [
      { text: 'Flip the switch at the top of this sidebar. He is told it is on and why it matters.' },
      { text: 'Reminders you asked for still arrive. Everything unprompted waits.' }
    ]
  },
  themes: {
    id: 'themes',
    title: 'Appearance',
    why: 'Pick the look of the app. Purely yours; he does not see it.',
    steps: [{ text: 'Open Appearance and choose a theme. It sticks to this browser.' }]
  }
};

export const HowTo: React.FC<{ guide: HowToGuide; defaultOpen?: boolean }> = ({ guide, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`howto ${open ? 'open' : ''}`}>
      <button className="howto-toggle" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="howto-icon">{open ? '▾' : '▸'}</span>
        <span className="howto-title">How to set this up</span>
      </button>
      {open && (
        <div className="howto-body">
          <p className="howto-why">{guide.why}</p>
          <ol className="howto-steps">
            {guide.steps.map((s, i) => (
              <li key={i}>
                <span>{s.text}</span>
                {s.detail && <div className="howto-detail">{s.detail}</div>}
              </li>
            ))}
          </ol>
          {guide.notes && guide.notes.length > 0 && (
            <ul className="howto-notes">
              {guide.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default HowTo;
